/**
 * Bootstrap entry point. Deliberately tiny (CLAUDE.md working rule #8 — "no god-files"): this
 * file's only job is to start the app shell, which owns routing/auth-guarding/page-mounting. It
 * does NOT construct a Babylon engine, touch the DOM beyond `#app-root`, or know about auth,
 * riders, or presets — see `app/app-shell.ts`, `auth/auth-service.ts`, `pages/*`.
 */
import { decideStaleBuildAction, isStaleBuildError, STALE_RELOAD_KEY } from "./stale-build";
import "./style.css";
// The public rider view's own layout (SCRUM-498) — after style.css, so it wins over the old windowed
// viewer's leftovers there.
import "./public-view/public-view.css";
import { startAppShell } from "./app/app-shell";
import { startMaintenanceBanner } from "./components/maintenance-banner";
import { initUiOverrides, loadActiveDictionary, t } from "./i18n";
import { fetchUiOverrides } from "./api/content-api";

/**
 * Render a fatal startup error INTO THE PAGE.
 *
 * Why this exists (owner report, 2026-07-20 — Safari showed a blank screen with an empty console):
 * when module evaluation throws, the SPA renders nothing and the only trace is a console entry that
 * appears exactly once, during load. Anyone who opens the inspector AFTER the blank page has
 * already rendered sees an empty console and has nothing at all to go on. A blank screen is the
 * worst possible failure report, so the app now reports its own death in the DOM, where it cannot
 * be missed and can be screenshotted.
 */
function showFatal(source: string, detail: string): void {
  const root = document.getElementById("app-root") ?? document.body;
  root.innerHTML = "";
  const box = document.createElement("div");
  box.setAttribute("data-scb-fatal", "1");
  box.style.cssText =
    "position:fixed;inset:0;z-index:9999;overflow:auto;padding:24px;background:#fff;color:#1c1d23;" +
    "font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif";
  const heading = document.createElement("h1");
  heading.style.cssText = "margin:0 0 8px;font-size:18px";
  heading.textContent = t("main.fatal.heading");
  const hint = document.createElement("p");
  hint.style.cssText = "margin:0 0 16px;color:#45474f";
  hint.textContent = t("main.fatal.hint");
  const pre = document.createElement("pre");
  pre.style.cssText =
    "white-space:pre-wrap;word-break:break-word;background:#f4f5f8;border:1px solid #d5d8df;" +
    "border-radius:8px;padding:14px;margin:0;font:12px/1.5 ui-monospace,monospace";
  // textContent, never innerHTML: the message can contain attacker-influenced strings (a URL, a
  // server response), and this overlay must not become an injection sink while reporting a crash.
  pre.textContent = `${source}\n\n${detail}\n\n${t("main.fatal.userAgentLabel", { ua: navigator.userAgent })}`;
  box.append(heading, hint, pre);
  root.appendChild(box);
}

/**
 * Устаревшая сборка в открытой вкладке: перезагрузить один раз вместо экрана «не запустилось».
 * Разбор и причина — `stale-build.ts`. Возвращает true, если перезагрузка начата и показывать
 * ничего не надо.
 */
function handledStaleBuild(err: unknown): boolean {
  if (!isStaleBuildError(err)) return false;
  let alreadyReloaded = false;
  let storageWorks = true;
  try {
    alreadyReloaded = window.sessionStorage.getItem(STALE_RELOAD_KEY) !== null;
  } catch {
    storageWorks = false;
  }
  if (decideStaleBuildAction({ alreadyReloaded, storageWorks }) !== "reload") return false;
  try {
    window.sessionStorage.setItem(STALE_RELOAD_KEY, String(Date.now()));
  } catch {
    return false; // защёлку поставить некуда — лучше честный экран, чем кольцо перезагрузок
  }
  window.location.reload();
  return true;
}

function isExtensionError(err: unknown): boolean {
  if (!err) return false;
  const str = err instanceof Error ? `${err.message}\n${err.stack ?? ""}` : String(err);
  return str.includes("chrome-extension://") || str.includes("moz-extension://") || str.includes("safari-extension://");
}

window.addEventListener("error", (event) => {
  if (isExtensionError(event.error) || isExtensionError(event.message)) return;
  if (handledStaleBuild(event.error) || handledStaleBuild(event.message)) return;
  showFatal("window.onerror", event.error instanceof Error ? `${event.error.message}\n${event.error.stack ?? ""}` : String(event.message));
});
window.addEventListener("unhandledrejection", (event) => {
  if (isExtensionError(event.reason)) return;
  if (handledStaleBuild(event.reason)) return;
  const reason = event.reason as unknown;
  showFatal("unhandledrejection", reason instanceof Error ? `${reason.message}\n${reason.stack ?? ""}` : String(reason));
});

// One deliberate boot line. It exists to disambiguate the single worst bug report there is — "blank
// screen, empty console" — which otherwise cannot distinguish "the bundle never executed" from "it
// executed fine and rendered something invisible". If this line is absent, the script never ran
// (cache, MIME type, CSP, a parse error the inspector missed); if it is present, the failure is
// downstream and visual, and the DOM under #app-root is where to look.
// eslint-disable-next-line no-console -- deliberate boot beacon; see the comment above
console.log("[scb] boot", { path: window.location.pathname, ua: navigator.userAgent });

// Admin-edited interface wording (SCRUM-43), started BEFORE the shell mounts: the cached copy is
// applied synchronously, the network refresh lands whenever it lands. Deliberately not awaited —
// the interface must never wait on this, and its failure mode is the wording we shipped.
initUiOverrides(fetchUiOverrides);

// СЛОВАРЬ АКТИВНОГО ЯЗЫКА — ОДИН, И ЕГО ЖДУТ ЗДЕСЬ (SCRUM-887). До 29.10.2026 в главный файл
// скриптов входили все шесть словарей сразу (81% его веса), хотя человек видит один. Теперь
// статический только `en`, остальные пять — отдельные файлы; переключение языка и так
// перезагружает страницу, поэтому на одну загрузку нужен ровно один словарь. Для английского это
// готовое значение и ни одного запроса. Не приехал (нет сети, упал файл) — показываем английский,
// а не пустой экран: отказ словаря не повод не запускать приложение.
void loadActiveDictionary()
  .catch(() => undefined)
  .then(() => {
    try {
      startAppShell();
      startMaintenanceBanner();
      // eslint-disable-next-line no-console -- the other half of the boot beacon
      console.log("[scb] app shell mounted");
    } catch (err) {
      showFatal("startAppShell", err instanceof Error ? `${err.message}\n${err.stack ?? ""}` : String(err));
    }
  });
